import React from 'react';
import './LandingPageContent.css';

const S3_ASSETS_BASE = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/landing';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

interface HowItWorksStepProps {
  number: number;
  title: string;
  description: string;
  image: string;
  imagePosition: 'left' | 'right';
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => (
  <div className="feature-card">
    <img src={`${S3_ASSETS_BASE}/${icon}`} alt={title} className="feature-icon" />
    <h3 className="feature-title">{title}</h3>
    <p className="feature-description">{description}</p>
  </div>
);

const HowItWorksStep: React.FC<HowItWorksStepProps> = ({
  number,
  title,
  description,
  image,
  imagePosition,
}) => (
  <div className={`how-it-works-step ${imagePosition === 'left' ? 'reverse' : ''}`}>
    <div className="step-content">
      <div className="step-number">{number}</div>
      <h3 className="step-title">{title}</h3>
      <p className="step-description">{description}</p>
    </div>
    <div className="step-image-container">
      <img src={`${S3_ASSETS_BASE}/${image}`} alt={title} className="step-image" />
    </div>
  </div>
);

const LandingPageContent: React.FC = () => {
  const handleStartTrading = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="landing-page-content">
      {/* Section 1: Peer-to-Peer trading, perfected */}
      <section className="section-features">
        <div className="section-features-inner">
          <h2 className="section-title">
            Peer-to-Peer trading, <span className="gradient-text">perfected</span>
          </h2>
          <div className="features-grid">
            <FeatureCard
              icon="icon-rocket.png"
              title="Asynchronous Trading"
              description="Trade on your schedule. No waiting for someone to want exactly what you have."
            />
            <FeatureCard
              icon="icon-shield.png"
              title="Built-In Protection"
              description="Admin case resolution, tracking requirements, verified accounts. Trade with confidence."
            />
            <FeatureCard
              icon="icon-lightning.png"
              title="Trade in Seconds"
              description="See a card you can send? Click, ship, done. No back-and-forth negotiation."
            />
            <FeatureCard
              icon="icon-bullseye.png"
              title="Get Exactly What You Want"
              description="Specify set, condition, language, foiling. No settling for &quot;close enough.&quot;"
            />
            <FeatureCard
              icon="icon-fees.png"
              title="Smallest Fees Around"
              description="Tiny 1-2% transaction fee. Keep 98%+ of your value vs. 50-85% elsewhere."
            />
            <FeatureCard
              icon="icon-globe.png"
              title="Growing Community"
              description="Active traders worldwide. More traders = faster matches = more cards."
            />
          </div>
        </div>
      </section>

      {/* Section 2: How It Works */}
      <section className="section-how-it-works">
        <div className="section-how-it-works-inner">
          <h2 className="section-title">How It Works</h2>
          <div className="how-it-works-steps">
            <HowItWorksStep
              number={1}
              title="Build your want list"
              description="Add cards you need for your Cube, Commander deck, or collection. Be as specific as you want—set, condition, foiling."
              image="mid-img-wantlist.png"
              imagePosition="right"
            />
            <HowItWorksStep
              number={2}
              title="Send Cards, Earn Points"
              description="Browse other players' want lists. Send them cards they need. When they arrive, earn points automatically. 100 points = $1."
              image="mid-img-send-cards.png"
              imagePosition="right"
            />
            <HowItWorksStep
              number={3}
              title="Spend Points on Your Wants"
              description="Use your points to get cards from your want list. No negotiation, no waiting for approval. Just trade."
              image="mid-img-spend-points.png"
              imagePosition="right"
            />
          </div>
        </div>
      </section>

      {/* Section 3: 100% Trade Guarantee */}
      <section className="section-guarantee">
        <div className="section-guarantee-inner">
          <img
            src={`${S3_ASSETS_BASE}/badge-trade-guarantee.png`}
            alt="100% Trade Guarantee Badge"
            className="guarantee-badge"
          />
          <h2 className="guarantee-title">100% Trade Guarantee</h2>
          <p className="guarantee-description">
            Nervous about trading by mail? Don't be. TradeMagic guarantees the success of every trade, or your points will be fully refunded.
          </p>
          <button className="cta-button" onClick={handleStartTrading}>
            Start Trading Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default LandingPageContent;
